/**
 * career-cv-proposal — the CV onboarding review (route `/onboarding/cv`).
 *
 * The extraction proposes a CV; NOTHING becomes the user's durable Lebenslauf until
 * they say so here. The proposal renders AS A CV — section headings, hairline-separated
 * entry rows, no boxed panels (guidelines §0 "seamless document over cards") — with each
 * row carrying a native checkbox (a genuine affordance) and an honest provenance cue:
 * the uploaded document it was read from, its verbatim line behind a native `<details>`
 * (adaptive disclosure), or »vom Modell ergänzt« when nothing in the documents resolves it.
 *
 * Approval posts the accepted keys and produces exactly ONE new CV version; the surface
 * then reads as the result, not as a form. Fetch-projection surface (owner data,
 * deliberate act — no live push).
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { hubHero, HUB_TITLE_ID } from "../lib/hub"
import { I18nToken, RouterToken, SessionToken, apiGet, apiPost } from "../services"
import type { ShellSession } from "../domain"
import {
  createCvProposalRejectionStore,
  rejectionsOf,
  selectionFor,
  type CvProposalRejectionStore,
} from "../lib/cv-proposal-draft"
import type { CareerI18n, CareerKey } from "../../i18n"
import type { Router } from "@platform/web-kit"

/** Verbatim attribution of one proposed entry to an uploaded document. */
interface EntrySource {
  documentId: string
  kind: string
  quote: string
}
interface ProposalEntry {
  key: string
  section: string
  title: string
  detail: string
  source: EntrySource | null
  required?: boolean
}
interface ApprovedView {
  version: number
  accepted: number
  rejected: number
}

/** Render order — the sections a reader expects a CV to open with come first. */
const SECTION_ORDER = [
  "basics",
  "work",
  "education",
  "projects",
  "skills",
  "languages",
  "certificates",
  "awards",
  "publications",
  "volunteer",
  "references",
  "interests",
] as const

const SECTION_KEY: Record<string, CareerKey> = {
  basics: "cvProposal.section.basics",
  work: "cvProposal.section.work",
  education: "cvProposal.section.education",
  projects: "cvProposal.section.projects",
  skills: "cvProposal.section.skills",
  languages: "cvProposal.section.languages",
  certificates: "cvProposal.section.certificates",
  awards: "cvProposal.section.awards",
  publications: "cvProposal.section.publications",
  volunteer: "cvProposal.section.volunteer",
  references: "cvProposal.section.references",
  interests: "cvProposal.section.interests",
}

const DOC_KIND_KEY: Record<string, CareerKey> = {
  cv: "cvProposal.doc.cv",
  letter: "cvProposal.doc.letter",
  reference: "cvProposal.doc.reference",
  certificate: "cvProposal.doc.certificate",
  other: "cvProposal.doc.other",
}

export class CvProposalVm implements ViewModel {
  readonly entries: Signal<readonly ProposalEntry[]> = signal<readonly ProposalEntry[]>([])
  /** The review keys currently ticked — the ONLY thing an approval sends. */
  readonly selected: Signal<ReadonlySet<string>> = signal<ReadonlySet<string>>(new Set())
  readonly proposedAt: Signal<string | null> = signal<string | null>(null)
  readonly approved: Signal<ApprovedView | null> = signal<ApprovedView | null>(null)
  readonly approving: Signal<boolean> = signal(false)
  readonly loaded: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  /** Per-user record of what the person took OUT — see `cv-proposal-draft`. */
  private readonly rejections: CvProposalRejectionStore

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
    session: ShellSession,
  ) {
    this.t = i18n.t
    this.rejections = createCvProposalRejectionStore(session.user?.sub ?? null)
    this.renderDeps = [this.entries, this.selected, this.proposedAt, this.approved, this.approving, this.loaded, this.error]
  }

  attach(): () => void {
    void this.refresh()
    return () => {}
  }

  /** Everything is pre-selected: the proposal is the model's best reading, and the
   *  user's job is to take things OUT, not to re-enter their own CV by hand. */
  private async refresh(): Promise<void> {
    try {
      const body = await apiGet<{ entries: ProposalEntry[]; proposedAt: string | null }>("/api/cv/proposal")
      this.entries.set(body.entries)
      // …minus whatever this person already took out of THIS proposal. Without that the
      // all-selected default silently re-accepts their rejections on the next approve.
      this.selected.set(selectionFor(body.entries, body.proposedAt, this.rejections.load()))
      this.proposedAt.set(body.proposedAt)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  /** A required entry (the name) can never be unticked — the CV cannot exist without it. */
  toggle(key: string): void {
    const entry = this.entries.value.find((e) => e.key === key)
    if (entry?.required) return
    this.selected.update((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    this.saveRejections()
  }

  setAll(on: boolean): void {
    this.selected.set(new Set(on ? this.entries.value.map((e) => e.key) : this.entries.value.filter((e) => e.required).map((e) => e.key)))
    this.saveRejections()
  }

  /** Mirror the decisions after every change — an untick exists nowhere else until approve. */
  private saveRejections(): void {
    const proposedAt = this.proposedAt.value
    if (proposedAt === null) return
    const rejected = rejectionsOf(this.entries.value, this.selected.value)
    if (rejected.length === 0) this.rejections.clear()
    else this.rejections.save({ proposedAt, rejected, updatedAt: new Date().toISOString() })
  }

  async approve(): Promise<void> {
    if (this.approving.value) return
    this.approving.set(true)
    this.error.set(null)
    try {
      const out = await apiPost<{ version: number; accepted: string[]; rejected: string[] }>("/api/cv/proposal/approve", {
        accepted: [...this.selected.value],
      })
      this.approved.set({ version: out.version, accepted: out.accepted.length, rejected: out.rejected.length })
      this.entries.set([])
      // The decisions are in the durable CV now — the local record has done its job.
      this.rejections.clear()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.approving.set(false)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerCvProposal extends KitElement<CvProposalVm> {
  protected createViewModel(ctx: KitContext): CvProposalVm {
    return new CvProposalVm(ctx.inject(RouterToken), ctx.inject(I18nToken), ctx.inject(SessionToken))
  }

  /** The provenance line: a real document + its verbatim line, or an honest "derived". */
  private provenance(vm: CvProposalVm, entry: ProposalEntry): TemplateResult {
    if (!entry.source) {
      return html`<span class="cv-prop-src is-derived" lang="de">${vm.t("cvProposal.derived")}</span>`
    }
    const kind = vm.t(DOC_KIND_KEY[entry.source.kind] ?? "cvProposal.doc.other")
    return html`
      <details class="cv-prop-src">
        <summary lang="de">${vm.t("cvProposal.source", { kind })}</summary>
        <p class="cv-prop-quote-label" lang="de">${vm.t("cvProposal.sourceQuote")}</p>
        <q class="cv-prop-quote">${entry.source.quote}</q>
      </details>
    `
  }

  /** The include/exclude affordance: a real <button> in the checkbox role (AX-3 —
   *  affordances are buttons), carrying the plan-rail marks the rest of the app uses. */
  private row(vm: CvProposalVm, entry: ProposalEntry): TemplateResult {
    const on = vm.selected.value.has(entry.key)
    const titleId = `cvp-${entry.key.replace(/[^a-zA-Z0-9]/g, "-")}`
    return html`
      <li class=${classMap({ "cv-prop-row": true, "is-out": !on, "is-required": Boolean(entry.required) })}>
        <button
          class="cv-prop-check"
          role="checkbox"
          aria-checked=${on ? "true" : "false"}
          aria-labelledby=${titleId}
          ?disabled=${Boolean(entry.required)}
          data-act="toggle-entry"
          data-key=${entry.key}
        >${on ? "✓" : "○"}</button>
        <span class="cv-prop-body">
          <span class="cv-prop-title" id=${titleId} lang="de">${entry.title}</span>
          ${entry.detail ? html`<span class="cv-prop-detail" lang="de">${entry.detail}</span>` : nothing}
        </span>
        <span class="cv-prop-meta">
          ${entry.required ? html`<span class="cv-prop-src is-required" lang="de">${vm.t("cvProposal.required")}</span>` : this.provenance(vm, entry)}
        </span>
      </li>
    `
  }

  private section(vm: CvProposalVm, name: string, entries: readonly ProposalEntry[]): TemplateResult {
    return html`
      <section class="cv-prop-section" aria-labelledby=${`cvp-sec-${name}`}>
        <h2 id=${`cvp-sec-${name}`} class="cv-prop-section-title" lang="de">${vm.t(SECTION_KEY[name] ?? "cvProposal.section.basics")}</h2>
        <ul class="cv-prop-rows" role="list">${repeat(entries, (e) => e.key, (e) => this.row(vm, e))}</ul>
      </section>
    `
  }

  /** The result state — after approval the surface reads as an outcome, not a form. */
  private done(vm: CvProposalVm, approved: ApprovedView): TemplateResult {
    return html`
      <section class="surface cv-prop" aria-labelledby=${HUB_TITLE_ID}>
        ${hubHero({
          eyebrow: vm.t("cvProposal.eyebrow"),
          title: vm.t("cvProposal.done.title"),
          lead: vm.t("cvProposal.done.body", { version: approved.version, accepted: approved.accepted, rejected: approved.rejected }),
          cta: { label: vm.t("cvProposal.done.toCv"), act: "nav", url: "/compass/cv" },
        })}
      </section>
    `
  }

  protected render(vm: CvProposalVm): TemplateResult {
    const approved = vm.approved.value
    if (approved) return this.done(vm, approved)

    const entries = vm.entries.value
    const err = vm.error.value
    const selected = vm.selected.value
    const n = selected.size

    if (vm.loaded.value && entries.length === 0) {
      return html`
        <section class="surface cv-prop" aria-labelledby=${HUB_TITLE_ID}>
          ${hubHero({
            eyebrow: vm.t("cvProposal.eyebrow"),
            title: vm.t("cvProposal.title"),
            lead: vm.t("cvProposal.empty"),
            cta: { label: vm.t("cvProposal.emptyCta"), act: "nav", url: "/onboarding" },
          })}
          ${err ? html`<p class="cv-prop-error" role="alert" lang="de">${vm.t("cvProposal.error", { message: err })}</p>` : nothing}
        </section>
      `
    }

    const date = vm.proposedAt.value ? new Date(vm.proposedAt.value).toLocaleDateString("de-DE") : "—"
    const sections = SECTION_ORDER.map((name) => ({ name, rows: entries.filter((e) => e.section === name) })).filter((s) => s.rows.length > 0)

    return html`
      <section class="surface cv-prop" aria-labelledby=${HUB_TITLE_ID}>
        ${hubHero({
          eyebrow: vm.t("cvProposal.eyebrow"),
          title: vm.t("cvProposal.title"),
          lead: vm.t("cvProposal.lead"),
          meta: vm.t("cvProposal.meta", { selected: n, total: entries.length, date }),
        })}
        ${err ? html`<p class="cv-prop-error" role="alert" lang="de">${vm.t("cvProposal.error", { message: err })}</p>` : nothing}

        <div class="cv-prop-bulk">
          <button class="hub-cta ghost" data-act="select-all" lang="de">${vm.t("cvProposal.selectAll")}</button>
          <button class="hub-cta ghost" data-act="select-none" lang="de">${vm.t("cvProposal.selectNone")}</button>
        </div>

        ${sections.map((s) => this.section(vm, s.name, s.rows))}

        <div class="cv-prop-actions">
          <button
            class=${classMap({ "hub-cta": true, "is-pending": vm.approving.value })}
            data-act="approve-proposal"
            ?disabled=${vm.approving.value}
            lang="de"
          >
            ${vm.approving.value ? vm.t("cvProposal.approving") : n === 1 ? vm.t("cvProposal.approveOne") : vm.t("cvProposal.approve", { n })}
          </button>
        </div>
      </section>
    `
  }

  protected override actions(vm: CvProposalVm): ActionMap {
    return {
      "toggle-entry": ({ key }) => { if (key) vm.toggle(key) },
      "select-all": () => vm.setAll(true),
      "select-none": () => vm.setAll(false),
      "approve-proposal": () => void vm.approve(),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
